import * as valueStyles from "./ColorPickerValue.css";

import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";

import { ColorPicker } from "./ColorPicker";
import {
  type ColorPickerDataProps,
  colorPickerDataSchema,
} from "./ColorPicker.schema";
import { ColorPickerArea } from "./ColorPickerArea";
import { ColorPickerContent } from "./ColorPickerContent";
import { ColorPickerHueSlider } from "./ColorPickerHueSlider";
import { ColorPickerSwatchGrid } from "./ColorPickerSwatchGrid";
import { ColorPickerTrigger } from "./ColorPickerTrigger";
import { ColorPickerValue } from "./ColorPickerValue";
import { ColorPickerValueSlider } from "./ColorPickerValueSlider";
import { ColorPickerWheel } from "./ColorPickerWheel";
import { COLOR_PICKER_TAG_NAME, DEFAULT_COLOR } from "./constants";

export interface ColorPickerHandle {
  value: (value: string) => void;
  disabled: (value?: boolean) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [COLOR_PICKER_TAG_NAME]: {
      props: ColorPickerDataProps;
      handle: ColorPickerHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-color-picker": HTMLElement & ColorPickerHandle;
  }
}

export function RWCColorPicker({
  ref,
  value: initialValue,
  disabled: initialDisabled,
  swatches,
  wheel,
  arrow,
  size,
  layout,
  ariaLabel,
  swatchesOnly,
}: ColorPickerDataProps & { ref?: Ref<ColorPickerHandle> }) {
  const host = useIslandHost();

  const [value, setValue] = useState(initialValue ?? DEFAULT_COLOR);
  const committed = useRef(value);
  const [disabled, setDisabled] = useState(initialDisabled ?? false);
  const [open, setOpen] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      value: (nextValue) => {
        committed.current = nextValue;
        setValue(nextValue);
      },
      disabled: (nextDisabled = true) => setDisabled(nextDisabled),
    }),
    [],
  );

  useEffect(() => {
    getElementInternals(host)?.setFormValue(disabled ? null : value);
  }, [host, value, disabled]);

  const swatchColors = useMemo(
    () =>
      swatches
        ?.split(",")
        .map((color) => color.trim())
        .filter(Boolean) ?? [],
    [swatches],
  );

  // Dragging changes the color on every frame: the page hears of it once,
  // when the popup closes on a new color
  function commit(nextValue: string) {
    if (nextValue === committed.current) {
      return;
    }

    committed.current = nextValue;
    host?.setAttribute("value", nextValue);
    host?.dispatchEvent(
      new CustomEvent("color-picker:change", {
        detail: { value: nextValue },
        bubbles: true,
      }),
    );
  }

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);

    if (!nextOpen) {
      commit(value);
    }
  }

  // A swatch is a choice made: with nothing else to adjust, it closes
  function handleValueChange(nextValue: string) {
    setValue(nextValue);

    if (swatchesOnly) {
      setOpen(false);
      commit(nextValue);
    }
  }

  return (
    <ColorPicker
      value={value}
      onValueChange={handleValueChange}
      open={open}
      onOpenChange={handleOpenChange}
      disabled={disabled}
      arrow={arrow}
      size={size}
    >
      <ColorPickerTrigger layout={layout} aria-label={ariaLabel}>
        <ColorPickerValue
          className={layout === "swatch" ? valueStyles.fill : undefined}
        />
      </ColorPickerTrigger>
      <ColorPickerContent>
        {swatchesOnly ? null : wheel ? (
          <>
            <ColorPickerWheel />
            <ColorPickerValueSlider />
          </>
        ) : (
          <>
            <ColorPickerArea />
            <ColorPickerHueSlider />
          </>
        )}
        {swatchColors.length > 0 && (
          <ColorPickerSwatchGrid colors={swatchColors} />
        )}
      </ColorPickerContent>
    </ColorPicker>
  );
}

componentManager.register(
  rwc(COLOR_PICKER_TAG_NAME, {
    component: RWCColorPicker,
    dataSchema: colorPickerDataSchema,
    formAssociated: true,
  }),
);
